import moment from 'moment';

export const SALES_DATE_MODES = {
    CURRENT_MONTH: 'current_month',
    LAST_TWO_MONTHS: 'last_two_months',
    DATE_RANGE: 'date_range',
};

const STORAGE_PREFIX = 'salesListSettings';

export function getSalesSettingsStorageKey(userId) {
    return `${STORAGE_PREFIX}_${userId || 'default'}`;
}

export function getDefaultSalesListSettings() {
    const now = moment();
    return {
        plantIds: [],
        customerIds: [],
        dateMode: SALES_DATE_MODES.CURRENT_MONTH,
        customFromDate: now.clone().startOf('month').format('DD-MM-YYYY'),
        customToDate: now.clone().endOf('month').format('DD-MM-YYYY'),
    };
}

export function normalizeDisplayDate(value) {
    if (value == null || value === '') {
        return null;
    }

    if (moment.isMoment(value) && value.isValid()) {
        return value.format('DD-MM-YYYY');
    }

    const trimmed = String(value).trim();
    if (!trimmed) {
        return null;
    }

    const strict = moment(trimmed, 'DD-MM-YYYY', true);
    if (strict.isValid()) {
        return strict.format('DD-MM-YYYY');
    }

    const cleaned = trimmed
        .replace(/[^\d-]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
    const parts = cleaned.split('-').filter(Boolean);

    if (parts.length !== 3) {
        return null;
    }

    const [dayPart, monthPart, yearPart] = parts;
    const day = dayPart.padStart(2, '0');
    const month = monthPart.padStart(2, '0');
    let year = yearPart;
    if (year.length === 2) {
        year = `20${year}`;
    }
    if (year.length !== 4) {
        return null;
    }

    const normalized = moment(`${day}-${month}-${year}`, 'DD-MM-YYYY', true);
    return normalized.isValid() ? normalized.format('DD-MM-YYYY') : null;
}

export function sanitizeSalesListSettings(settings) {
    const defaults = getDefaultSalesListSettings();
    const next = {
        ...defaults,
        ...settings,
        plantIds: Array.isArray(settings?.plantIds) ? settings.plantIds : defaults.plantIds,
        customerIds: Array.isArray(settings?.customerIds) ? settings.customerIds : defaults.customerIds,
    };

    if (next.dateMode !== SALES_DATE_MODES.DATE_RANGE) {
        return next;
    }

    const from = normalizeDisplayDate(next.customFromDate);
    const to = normalizeDisplayDate(next.customToDate);

    if (!from || !to) {
        next.customFromDate = defaults.customFromDate;
        next.customToDate = defaults.customToDate;
        return next;
    }

    const fromMoment = moment(from, 'DD-MM-YYYY', true);
    const toMoment = moment(to, 'DD-MM-YYYY', true);

    next.customFromDate = from;
    next.customToDate = fromMoment.isAfter(toMoment) ? from : to;

    return next;
}

export function loadSalesListSettings(userId) {
    try {
        const raw = window.localStorage.getItem(getSalesSettingsStorageKey(userId));
        if (!raw) {
            return getDefaultSalesListSettings();
        }
        const parsed = JSON.parse(raw);
        return sanitizeSalesListSettings({
            ...getDefaultSalesListSettings(),
            ...parsed,
        });
    } catch (error) {
        return getDefaultSalesListSettings();
    }
}

export function saveSalesListSettings(userId, settings) {
    window.localStorage.setItem(
        getSalesSettingsStorageKey(userId),
        JSON.stringify(sanitizeSalesListSettings(settings)),
    );
}

export function getDateRangeFromSalesSettings(settings) {
    const now = moment();
    switch (settings?.dateMode) {
        case SALES_DATE_MODES.LAST_TWO_MONTHS:
            return [
                now.clone().subtract(1, 'month').startOf('month'),
                now.clone().endOf('month'),
            ];
        case SALES_DATE_MODES.DATE_RANGE: {
            const from = normalizeDisplayDate(settings.customFromDate);
            const to = normalizeDisplayDate(settings.customToDate);
            if (from && to) {
                const fromMoment = moment(from, 'DD-MM-YYYY', true);
                const toMoment = moment(to, 'DD-MM-YYYY', true);
                if (fromMoment.isAfter(toMoment)) {
                    return [fromMoment, fromMoment];
                }
                return [fromMoment, toMoment];
            }
            return [now.clone().startOf('month'), now.clone().endOf('month')];
        }
        case SALES_DATE_MODES.CURRENT_MONTH:
        default:
            return [now.clone().startOf('month'), now.clone().endOf('month')];
    }
}

export function resolvePlantFilter(settings, allowedPlants) {
    const allowedIds = allowedPlants.map((plant) => String(plant.value));
    const selectedIds = (settings.plantIds || [])
        .map((id) => String(id))
        .filter((id) => allowedIds.includes(id));

    if (!selectedIds.length || selectedIds.length === allowedIds.length) {
        return '*';
    }
    return selectedIds.join(',');
}

export function resolveCustomerFilter(settings, customers) {
    const allIds = customers.map((customer) => String(customer.value));
    const selectedIds = (settings.customerIds || [])
        .map((id) => String(id))
        .filter((id) => allIds.includes(id));

    if (!customers.length || !selectedIds.length || selectedIds.length === allIds.length) {
        return '*';
    }
    return selectedIds.join(',');
}

export function getSalesSettingsSummary(settings, allowedPlants, customers, options = {}) {
    const [fromDate, toDate] = getDateRangeFromSalesSettings(settings);
    const plantCount = settings.plantIds?.length;
    const customerCount = settings.customerIds?.length;
    const plantLabel = !plantCount || plantCount === allowedPlants.length
        ? `All plants (${allowedPlants.length})`
        : `${plantCount} plant(s)`;
    const hideCustomers = options.hideCustomers || !customers || customers.length === 0;
    const customerLabel = hideCustomers
        ? null
        : (!customerCount || customerCount === customers.length
            ? `All customers (${customers.length})`
            : `${customerCount} customer(s)`);

    let dateLabel = 'Current month';
    if (settings.dateMode === SALES_DATE_MODES.LAST_TWO_MONTHS) {
        dateLabel = 'Last two months';
    } else if (settings.dateMode === SALES_DATE_MODES.DATE_RANGE) {
        dateLabel = `${fromDate.format('DD-MM-YYYY')} to ${toDate.format('DD-MM-YYYY')}`;
    }

    return [plantLabel, customerLabel, dateLabel].filter(Boolean).join(' · ');
}
